import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FaDownload, FaCheck, FaImages, FaTrash, FaEdit,
  FaExpand, FaPlay, FaTimes, FaArrowLeft, FaArrowRight,
  FaPause, FaEyeSlash, FaEye, FaCheckSquare, FaSquare, FaSearch, FaUser,
  FaShoppingCart, FaCreditCard, FaHeart, FaTag, FaCamera,
} from 'react-icons/fa';
import {
  browseEventPhotos, downloadMultiplePhotos, downloadPhoto,
  deletePhoto, getEvent, deleteEvent, fetchPhotoBlob, getFlaskUploadUrl, scanEventForMyFace,
  checkoutPhotoCart, getImageUrl, likePhoto, unlikePhoto, bulkSetPhotoPrice, uploadProfileEmbedding,
} from '../services/api';
import PhotoEditModal from '../components/PhotoEditModal';
import { compressImageForUpload } from '../utils/imageCompression';

const getPhotoDisplayName = (photo) => {
  const name = photo?.name || photo?.title || photo?.filename || `Photo ${photo?.id || ''}`;
  return name.split('/').pop() || name;
};

const toNumber = (value) => Number(value) || 0;
const IMAGE_LOAD_CONCURRENCY = 4;
const PAYMENT_METHODS = {
  visa: 'Carte Visa',
  wave_ci: "Wave Côte d'Ivoire",
  orange_money_ci: "Orange Money Côte d'Ivoire",
};
const canDownloadPhoto = (photo, isEventOwner) => (
  isEventOwner
  || Boolean(photo?.has_purchased)
);

const EventBrowse = () => {
  const { user } = useAuth();
  const { items: cartItems, toggleItem: toggleCartItem, removeItem: removeCartItem, clearCart } = useCart();
  const { eventId } = useParams();
  const navigate = useNavigate();

  const [event, setEvent] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });
  const [editingPhoto, setEditingPhoto] = useState(null);
  const [deletingPhoto, setDeletingPhoto] = useState(null);
  const [updatingPrice, setUpdatingPrice] = useState(false);
  const [photoBlobs, setPhotoBlobs] = useState({});
  const [likingPhotoIds, setLikingPhotoIds] = useState([]);
  const [checkingOut, setCheckingOut] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [paymentData, setPaymentData] = useState({
    method: 'visa',
    cardholderName: '',
    cardNumber: '',
    expiry: '',
    cvc: '',
    billingEmail: user?.email || '',
    mobileNumber: '',
  });
  const [paymentError, setPaymentError] = useState('');

  // Face scan state (Flask DeepFace service)
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [showOnlyMatches, setShowOnlyMatches] = useState(false);
  const [deepFaceModel, setDeepFaceModel] = useState('ArcFace');
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraLoading, setCameraLoading] = useState(false);
  const [capturedFacePreview, setCapturedFacePreview] = useState(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const DEEPFACE_MODELS = ['ArcFace', 'VGG-Face', 'Facenet512', 'Facenet', 'SFace', 'GhostFaceNet'];

  // Lightbox / slideshow state
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [slideshowActive, setSlideshowActive] = useState(false);

  const isPhotographer = user?.role === 'photographer';
  const isEventOwner = isPhotographer
    && event?.photographer_id != null
    && Number(event.photographer_id) === Number(user?.id);
  const cartPhotoIds = cartItems.map(item => item.id);

  useEffect(() => {
    fetchAll();
  }, [eventId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => stopFaceCamera();
  }, []);

  useEffect(() => {
    if (cameraActive && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
    }
  }, [cameraActive]);

  // Slideshow auto-advance
  useEffect(() => {
    if (!slideshowActive || lightboxIndex === null || photos.length === 0) return;
    const timer = setInterval(() => {
      setLightboxIndex(prev => (prev + 1) % photos.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [slideshowActive, lightboxIndex, photos.length]);

  // Keyboard navigation for lightbox
  const handleKeyDown = useCallback((e) => {
    if (lightboxIndex === null) return;
    if (e.key === 'ArrowRight') setLightboxIndex(prev => (prev + 1) % photos.length);
    if (e.key === 'ArrowLeft') setLightboxIndex(prev => (prev - 1 + photos.length) % photos.length);
    if (e.key === 'Escape') closeLightbox();
  }, [lightboxIndex, photos.length]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const fetchImageBlob = async (photo) => {
    try {
      const blobUrl = await fetchPhotoBlob(photo.id);
      setPhotoBlobs(prev => ({ ...prev, [photo.id]: blobUrl }));
    } catch (err) {
      if (photo.filename) {
        setPhotoBlobs(prev => ({ ...prev, [photo.id]: getFlaskUploadUrl(photo.filename) }));
      }
      console.error('Error loading image blob:', err);
    }
  };

  const fetchImageBlobs = async (items) => {
    let nextIndex = 0;
    const workerCount = Math.min(IMAGE_LOAD_CONCURRENCY, items.length);
    const workers = Array.from({ length: workerCount }, async () => {
      while (nextIndex < items.length) {
        const photo = items[nextIndex];
        nextIndex += 1;
        await fetchImageBlob(photo);
      }
    });

    await Promise.all(workers);
  };

  const fetchAll = async () => {
    try {
      const [eventRes, photosRes] = await Promise.all([
        getEvent(eventId).catch(() => null),
        browseEventPhotos(eventId),
      ]);
      if (eventRes) {
        setEvent(eventRes.event || eventRes.data?.event || eventRes);
      }
      const fetchedPhotos = photosRes.photos || [];
      setPhotos(fetchedPhotos);
      fetchImageBlobs(fetchedPhotos);
    } catch (err) {
      setError(err.message || 'Impossible de charger les photos');
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoSaved = (updatedPhoto) => {
    setPhotos(prev => prev.map(p =>
      p.id === updatedPhoto.id ? { ...p, ...updatedPhoto } : p
    ));
    setMessage({ type: 'success', text: 'Photo updated successfully!' });
  };

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm('Supprimer cette photo ? Cette action est irréversible.')) return;
    setDeletingPhoto(photoId);
    setMessage({ type: '', text: '' });
    try {
      await deletePhoto(photoId);
      setPhotos(prev => prev.filter(p => p.id !== photoId));
      setSelectedPhotos(prev => prev.filter(id => id !== photoId));
      removeCartItem(photoId);
      setMessage({ type: 'success', text: 'Photo deleted.' });
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Impossible de supprimer la photo' });
    } finally {
      setDeletingPhoto(null);
    }
  };

  const handleToggleLike = async (photo) => {
    if (likingPhotoIds.includes(photo.id)) return;

    const wasLiked = Boolean(photo.is_liked);
    const optimisticLikes = Math.max(0, toNumber(photo.likes_count) + (wasLiked ? -1 : 1));
    setLikingPhotoIds(prev => [...prev, photo.id]);
    setPhotos(prev => prev.map(p => (
      p.id === photo.id
        ? {
            ...p,
            is_liked: !wasLiked,
            likes_count: optimisticLikes,
          }
        : p
    )));

    try {
      const response = wasLiked
        ? await unlikePhoto(photo.id)
        : await likePhoto(photo.id);

      setPhotos(prev => prev.map(p => (
        p.id === photo.id
          ? {
              ...p,
              is_liked: Boolean(response.liked),
              likes_count: toNumber(response.likes_count),
            }
          : p
      )));
    } catch (err) {
      setPhotos(prev => prev.map(p => (
        p.id === photo.id
          ? {
              ...p,
              is_liked: wasLiked,
              likes_count: toNumber(photo.likes_count),
            }
          : p
      )));
      setMessage({ type: 'error', text: err.message || "Impossible de mettre à jour la mention J'aime" });
    } finally {
      setLikingPhotoIds(prev => prev.filter(id => id !== photo.id));
    }
  };

  const handleDeleteEvent = async () => {
    if (!window.confirm('Supprimer tout cet événement et toutes ses photos ? Cette action est irréversible.')) return;
    try {
      await deleteEvent(eventId);
      navigate('/photographer', {
        state: { successMessage: "Événement supprimé avec succès." }
      });
    } catch (err) {
      setMessage({ type: 'error', text: err.message || "Impossible de supprimer l'événement" });
    }
  };

  const togglePhotoSelection = (photoId) => {
    setSelectedPhotos(prev =>
      prev.includes(photoId) ? prev.filter(id => id !== photoId) : [...prev, photoId]
    );
  };

  const selectAll = () => {
    setSelectedPhotos(selectedPhotos.length === photos.length ? [] : photos.map(p => p.id));
  };

  const triggerBlobDownload = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadSingle = async (photo) => {
    if (!canDownloadPhoto(photo, isPhotographer)) {
      setMessage({ type: 'error', text: 'Achetez cette photo avant de la télécharger.' });
      return;
    }

    try {
      const blob = await downloadPhoto(photo.id);
      triggerBlobDownload(blob, getPhotoDisplayName(photo) || `photo_${photo.id}`);
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Le téléchargement a échoué' });
    }
  };

  const toggleCartPhoto = (photo) => {
    toggleCartItem(photo, eventId);
  };

  const markPurchased = (photoIds) => {
    setPhotos(prev => prev.map(photo =>
      photoIds.includes(photo.id) ? { ...photo, has_purchased: true } : photo
    ));
  };

  useEffect(() => {
    const handleCartCheckoutSuccess = (event) => {
      const photoIds = event.detail?.photoIds || [];
      if (photoIds.length === 0) return;

      markPurchased(photoIds);
      setMessage({
        type: 'success',
        text: `Achat terminé avec ${event.detail?.methodLabel || 'paiement'}. Vous pouvez maintenant télécharger vos photos.`,
      });
    };

    window.addEventListener('photomatch:cart-checkout-success', handleCartCheckoutSuccess);
    return () => window.removeEventListener('photomatch:cart-checkout-success', handleCartCheckoutSuccess);
  }, []);

  const handlePaymentChange = (field, value) => {
    setPaymentData(prev => ({ ...prev, [field]: value }));
    setPaymentError('');
  };

  const validatePaymentForm = () => {
    if (paymentData.method === 'wave_ci' || paymentData.method === 'orange_money_ci') {
      const digits = paymentData.mobileNumber.replace(/\D/g, '');
      if (digits.length < 8 || digits.length > 14) {
        return 'Saisissez un numéro mobile money valide.';
      }

      return '';
    }

    const digits = paymentData.cardNumber.replace(/\D/g, '');
    const cvcDigits = paymentData.cvc.replace(/\D/g, '');
    const expiryMatch = paymentData.expiry.match(/^(0[1-9]|1[0-2])\/\d{2}$/);

    if (!paymentData.cardholderName.trim()) return 'Saisissez le nom du titulaire.';
    if (digits.length < 13 || digits.length > 19) return 'Saisissez un numéro de carte valide.';
    if (!expiryMatch) return "Utilisez MM/AA pour la date d'expiration.";
    if (cvcDigits.length < 3 || cvcDigits.length > 4) return 'Saisissez un CVC valide.';
    if (!paymentData.billingEmail.trim() || !paymentData.billingEmail.includes('@')) {
      return 'Saisissez un e-mail de facturation valide.';
    }

    return '';
  };

  const handleCheckoutCart = async (e) => {
    e?.preventDefault();

    if (cartPhotoIds.length === 0) {
      setMessage({ type: 'error', text: 'Votre panier est vide' });
      setShowPaymentForm(false);
      return;
    }

    const validationError = validatePaymentForm();
    if (validationError) {
      setPaymentError(validationError);
      return;
    }

    setCheckingOut(true);
    setMessage({ type: '', text: '' });
    setPaymentError('');
    try {
      await checkoutPhotoCart(cartPhotoIds, {
        method: paymentData.method,
        mobile_number: paymentData.mobileNumber,
        billing_email: paymentData.billingEmail,
      });
      markPurchased(cartPhotoIds);
      clearCart();
      setShowPaymentForm(false);
      setPaymentData(prev => ({
        ...prev,
        cardNumber: '',
        expiry: '',
        cvc: '',
        mobileNumber: '',
      }));
      const methodLabel = PAYMENT_METHODS[paymentData.method] || 'paiement';
      setMessage({ type: 'success', text: `Achat terminé avec ${methodLabel}. Vous pouvez maintenant télécharger vos photos.` });
    } catch (err) {
      setPaymentError(err.message || 'Le paiement a échoué');
    } finally {
      setCheckingOut(false);
    }
  };

  const handleDownloadSelected = async () => {
    if (selectedPhotos.length === 0) {
      setMessage({ type: 'error', text: 'Veuillez sélectionner au moins une photo' });
      return;
    }

    const downloadablePhotoIds = photos
      .filter(photo => selectedPhotos.includes(photo.id) && canDownloadPhoto(photo, isEventOwner))
      .map(photo => photo.id);

    if (downloadablePhotoIds.length === 0) {
      setMessage({ type: 'error', text: 'Sélectionnez des photos gratuites ou achetées à télécharger.' });
      return;
    }

    setDownloading(true);
    setMessage({ type: '', text: '' });
    try {

      console.log("Requesting download for photo IDs:", downloadablePhotoIds);
      
      const blob = await downloadMultiplePhotos({ photo_ids: downloadablePhotoIds });

      console.log("Blob received for multiple photos:", blob);
      triggerBlobDownload(blob, `event_${eventId}_photos.zip`);
      setMessage({ type: 'success', text: `${downloadablePhotoIds.length} photos téléchargées avec succès !` });
      setSelectedPhotos([]);
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Le téléchargement a échoué' });
    } finally {
      setDownloading(false);
    }
  };

  const handleBulkSetPrice = async () => {
    if (!isEventOwner) {
      setMessage({ type: 'error', text: 'Only the event owner can update photo prices.' });
      return;
    }

    if (selectedPhotos.length === 0) {
      setMessage({ type: 'error', text: 'Sélectionnez au moins une photo pour mettre à jour le prix.' });
      return;
    }

    const priceInput = window.prompt('Définir le prix des photos sélectionnées', '0');
    if (priceInput === null) return;

    const normalizedPrice = Number(priceInput.replace(',', '.'));
    if (!Number.isFinite(normalizedPrice) || normalizedPrice < 0) {
      setMessage({ type: 'error', text: 'Saisissez un prix valide de 0 ou plus.' });
      return;
    }

    setUpdatingPrice(true);
    setMessage({ type: '', text: '' });
    try {
      const response = await bulkSetPhotoPrice(selectedPhotos, normalizedPrice);
      const updatedIds = response.photo_ids || selectedPhotos;
      const nextPrice = Number(response.price ?? normalizedPrice);
      const isPurchasable = Boolean(response.is_purchasable ?? nextPrice > 0);

      setPhotos(prev => prev.map(photo => (
        updatedIds.includes(photo.id)
          ? { ...photo, price: nextPrice, is_purchasable: isPurchasable }
          : photo
      )));
      setMessage({ type: 'success', text: `Prix mis à jour pour ${updatedIds.length} photo${updatedIds.length !== 1 ? 's' : ''}.` });
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Impossible de mettre à jour les prix' });
    } finally {
      setUpdatingPrice(false);
    }
  };

  const startFaceCamera = async () => {
    setMessage({ type: '', text: '' });
    setCameraLoading(true);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'user',
          width: { ideal: 960 },
          height: { ideal: 720 },
        },
        audio: false,
      });

      streamRef.current = stream;
      setCameraActive(true);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      setMessage({
        type: 'error',
        text: "Impossible d'accéder à votre webcam. Autorisez l'accès à la caméra puis réessayez.",
      });
    } finally {
      setCameraLoading(false);
    }
  };

  const stopFaceCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    setCameraActive(false);
  };

  const captureFacePhoto = () => new Promise((resolve, reject) => {
    const video = videoRef.current;
    if (!video || video.readyState < 2) {
      reject(new Error("La caméra n'est pas encore prête."));
      return;
    }

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth || 960;
    canvas.height = video.videoHeight || 720;
    const context = canvas.getContext('2d');
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error("Impossible de capturer une image depuis la webcam."));
        return;
      }

      resolve(new File([blob], `webcam-face-${Date.now()}.jpg`, { type: 'image/jpeg' }));
    }, 'image/jpeg', 0.92);
  });

  const runFaceScan = async ({ photoFile = null } = {}) => {
    setScanning(true);
    setScanResult(null);
    setMessage({ type: '', text: '' });
    try {
      if (photoFile) {
        const uploadFile = await compressImageForUpload(photoFile, undefined, undefined, 'Photo webcam');
        const formData = new FormData();
        formData.append('photo', uploadFile);
        await uploadProfileEmbedding(formData, deepFaceModel);
        setCapturedFacePreview(URL.createObjectURL(uploadFile));
      }

      const result = await scanEventForMyFace(eventId, deepFaceModel);
      setScanResult(result);
      setShowOnlyMatches(true);
      setMessage({ type: 'success', text: result.message || 'Recherche terminée.' });
    } catch (err) {
      const detail = err.error && err.error !== err.message ? `: ${err.error}` : '';
      setMessage({
        type: 'error',
        text: `${err.message || "Le scan du visage a échoué. Assurez-vous que votre visage est bien visible."}${detail}`
      });
    } finally {
      setScanning(false);
    }
  };

  const handleScanMyFace = () => runFaceScan();

  const handleScanWithWebcam = async () => {
    try {
      const photoFile = await captureFacePhoto();
      stopFaceCamera();
      await runFaceScan({ photoFile });
    } catch (err) {
      setMessage({ type: 'error', text: err.message || "Impossible de scanner votre visage avec la webcam." });
    }
  };

  const matchedPhotoIds = new Set((scanResult?.matched_photos || []).map(p => p.id));
  const displayedPhotos = showOnlyMatches && scanResult
    ? photos.filter(p => matchedPhotoIds.has(p.id))
    : photos;

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setSlideshowActive(false);
  };

  const closeLightbox = () => {
    setLightboxIndex(null);
    setSlideshowActive(false);
  };

  const lightboxPrev = () => setLightboxIndex(prev => (prev - 1 + photos.length) % photos.length);
  const lightboxNext = () => setLightboxIndex(prev => (prev + 1) % photos.length);

  if (loading) {
    return (
      <div className="container" style={{ textAlign: 'center', padding: '4rem' }}>
        <div className="spinner"></div>
        <p>Chargement des photos...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container">
        <div className="error-message">{error}</div>
      </div>
    );
  }

  const currentPhoto = lightboxIndex !== null ? photos[lightboxIndex] : null;
  const cartPhotos = cartItems;
  const cartTotal = cartPhotos.reduce((sum, photo) => sum + Number(photo.price || 0), 0);
  const activePaymentLabel = PAYMENT_METHODS[paymentData.method] || PAYMENT_METHODS.visa;
  const waveQrPayload = `WAVE-CI|event:${eventId}|amount:${cartTotal.toFixed(2)}|items:${cartPhotoIds.length}|phone:${paymentData.mobileNumber || 'pending'}`;
  const waveQrCells = Array.from({ length: 81 }, (_, index) => {
    const code = waveQrPayload.charCodeAt(index % waveQrPayload.length) || 0;
    const row = Math.floor(index / 9);
    const col = index % 9;
    const inCorner = (row < 3 && col < 3) || (row < 3 && col > 5) || (row > 5 && col < 3);
    return inCorner || ((code + index + row * col) % 3 === 0);
  });
  const coverPhotoUrl = event?.cover_photo ? getImageUrl(event.cover_photo) : null;
  const selectedDownloadableCount = photos.filter(photo =>
    selectedPhotos.includes(photo.id) && canDownloadPhoto(photo, isEventOwner)
  ).length;

  return (
    <>
      <div className="container">
        <div style={{
          width: '100%',
          height: '260px',
          borderRadius: '14px',
          overflow: 'hidden',
          marginBottom: '1.5rem',
          background: 'linear-gradient(135deg, var(--primary-orange), var(--primary-green))',
          boxShadow: '0 4px 14px var(--shadow)',
          position: 'relative',
        }}>
          {coverPhotoUrl ? (
            <img
              src={coverPhotoUrl}
              alt={`${event?.name || 'Event'} cover`}
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          ) : (
            <div style={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'rgba(255,255,255,0.9)',
              fontSize: '4rem',
            }}>
              <FaImages />
            </div>
          )}
          <div style={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            padding: '2rem',
            color: 'white',
            background: 'linear-gradient(to top, rgba(0,0,0,0.65), transparent)',
          }}>
            <h2 style={{ margin: 0, fontSize: '1.8rem' }}>{event?.name || "Photos de l'événement"}</h2>
          </div>
        </div>

        {/* Header */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
          marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem'
        }}>
          <div>
            <h1 style={{ color: 'var(--primary-orange)', marginBottom: '0.25rem' }}>
              {event?.name || "Photos de l'événement"}
            </h1>
            {event?.location && (
              <p style={{ color: 'var(--gray)', margin: '0 0 0.25rem' }}>📍 {event.location}</p>
            )}
            {event?.event_date && (
              <p style={{ color: 'var(--gray)', margin: '0 0 0.25rem' }}>
                📅 {new Date(event.event_date).toLocaleDateString()}
              </p>
            )}
            <p style={{ color: 'var(--gray)', margin: 0 }}>
              {photos.length} photos • {selectedPhotos.length} selected
            </p>
          </div>

          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
            {!isPhotographer && (
              <>
                <button
                  onClick={handleScanMyFace}
                  disabled={scanning}
                  style={{
                    padding: '0.7rem 1.2rem', background: scanning ? 'var(--gray)' : '#7c3aed',
                    color: 'white', border: 'none', borderRadius: '8px',
                    cursor: scanning ? 'not-allowed' : 'pointer', fontWeight: '600',
                    display: 'flex', alignItems: 'center', gap: '0.4rem',
                  }}
                >
                  <FaSearch /> {scanning ? 'Scan...' : 'Trouver mes photos'}
                </button>

                <button
                  onClick={cameraActive ? stopFaceCamera : startFaceCamera}
                  disabled={scanning || cameraLoading}
                  style={{
                    padding: '0.7rem 1.2rem',
                    background: cameraActive ? '#f44336' : '#0f172a',
                    color: 'white', border: 'none', borderRadius: '8px',
                    cursor: scanning || cameraLoading ? 'not-allowed' : 'pointer',
                    fontWeight: '600',
                    display: 'flex', alignItems: 'center', gap: '0.4rem',
                  }}
                >
                  {cameraActive ? <FaTimes /> : <FaCamera />}
                  {cameraLoading ? 'Ouverture...' : cameraActive ? 'Fermer webcam' : 'Scanner webcam'}
                </button>

                <select
                  value={deepFaceModel}
                  onChange={e => setDeepFaceModel(e.target.value)}
                  disabled={scanning}
                  style={{
                    padding: '0.7rem 0.6rem', background: '#0f172a', color: '#a78bfa',
                    border: 'none', borderRadius: '8px', fontWeight: '600', fontSize: '0.85rem',
                    cursor: scanning ? 'not-allowed' : 'pointer',
                  }}
                >
                  {DEEPFACE_MODELS.map(m => <option key={m} value={m}>{m}</option>)}
                </select>

                {scanResult && (
                  <button
                    onClick={() => setShowOnlyMatches(s => !s)}
                    style={{
                      padding: '0.7rem 1.2rem',
                      background: showOnlyMatches ? '#7c3aed' : 'rgba(124,58,237,0.15)',
                      color: showOnlyMatches ? 'white' : '#7c3aed',
                      border: '2px solid #7c3aed', borderRadius: '8px',
                      cursor: 'pointer', fontWeight: '600',
                      display: 'flex', alignItems: 'center', gap: '0.4rem',
                    }}
                  >
                    <FaUser /> {showOnlyMatches ? 'Tout afficher' : `Mes correspondances (${scanResult.matched_photos.length})`}
                  </button>
                )}
              </>
            )}
            {isEventOwner && (
              <>
                <button
                  onClick={() => navigate(`/event/${eventId}/edit`)}
                  style={{
                    padding: '0.7rem 1.2rem', background: 'var(--primary-orange)',
                    color: 'white', border: 'none', borderRadius: '8px',
                    cursor: 'pointer', fontWeight: '600',
                    display: 'flex', alignItems: 'center', gap: '0.4rem',
                  }}
                >
                <FaEdit /> Modifier l'événement
                </button>
                <button
                  onClick={handleDeleteEvent}
                  style={{
                    padding: '0.7rem 1.2rem', background: '#f44336',
                    color: 'white', border: 'none', borderRadius: '8px',
                    cursor: 'pointer', fontWeight: '600',
                    display: 'flex', alignItems: 'center', gap: '0.4rem',
                  }}
                >
                  <FaTrash /> Supprimer l'événement
                </button>
              </>
            )}
            {photos.length > 0 && (
              <button
                onClick={() => { openLightbox(0); setSlideshowActive(true); }}
                style={{
                  padding: '0.7rem 1.2rem', background: 'var(--primary-green)',
                  color: 'white', border: 'none', borderRadius: '8px',
                  cursor: 'pointer', fontWeight: '600',
                  display: 'flex', alignItems: 'center', gap: '0.4rem',
                }}
              >
                <FaPlay /> Slideshow
              </button>
            )}
            <button
              onClick={selectAll}
              style={{
                padding: '0.7rem 1.2rem', background: 'var(--primary-green)',
                color: 'white', border: 'none', borderRadius: '8px',
                cursor: 'pointer', fontWeight: '600',
                display: 'flex', alignItems: 'center', gap: '0.4rem',
              }}
            >
              <FaCheck /> {selectedPhotos.length === photos.length ? 'Tout désélectionner' : 'Tout sélectionner'}
            </button>
            {selectedDownloadableCount > 0 && (
              <button
                onClick={handleDownloadSelected}
                disabled={downloading}
                style={{
                  padding: '0.7rem 1.2rem',
                  background: 'var(--primary-orange)',
                  color: 'white', border: 'none', borderRadius: '8px',
                  cursor: downloading ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  display: 'flex', alignItems: 'center', gap: '0.4rem',
                }}
              >
                <FaDownload /> {downloading ? 'Téléchargement...' : `Télécharger (${selectedDownloadableCount})`}
              </button>
            )}
            {isEventOwner && selectedPhotos.length > 0 && (
              <button
                onClick={handleBulkSetPrice}
                disabled={updatingPrice}
                style={{
                  padding: '0.7rem 1.2rem',
                  background: updatingPrice ? 'var(--gray)' : 'var(--primary-orange)',
                  color: 'white', border: 'none', borderRadius: '8px',
                  cursor: updatingPrice ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  display: 'flex', alignItems: 'center', gap: '0.4rem',
                }}
              >
                <FaTag /> {updatingPrice ? 'Enregistrement...' : `Définir le prix (${selectedPhotos.length})`}
              </button>
            )}
          </div>
        </div>

        {message.text && (
          <div className={message.type === 'success' ? 'success-message' : 'error-message'}>
            {message.text}
          </div>
        )}

        {!isPhotographer && cameraActive && (
          <div style={{
            marginBottom: '1.5rem',
            padding: '1rem',
            background: 'var(--white)',
            border: '1px solid var(--light-gray)',
            borderRadius: '12px',
            boxShadow: '0 3px 10px var(--shadow)',
          }}>
            <div style={{
              display: 'flex',
              gap: '1rem',
              alignItems: 'center',
              flexWrap: 'wrap',
            }}>
              <div style={{
                borderRadius: '10px',
                overflow: 'hidden',
                background: '#0f172a',
                minHeight: '220px',
                flex: '1 1 280px',
              }}>
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  style={{
                    width: '100%',
                    maxHeight: '360px',
                    objectFit: 'cover',
                    display: 'block',
                  }}
                />
              </div>

              <button
                onClick={handleScanWithWebcam}
                disabled={scanning}
                style={{
                  padding: '0.8rem 1.2rem',
                  background: scanning ? 'var(--gray)' : '#7c3aed',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: scanning ? 'not-allowed' : 'pointer',
                  fontWeight: '700',
                  minHeight: '48px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.45rem',
                  whiteSpace: 'nowrap',
                  flex: '1 1 190px',
                }}
              >
                <FaSearch /> {scanning ? 'Recherche...' : 'Capturer et chercher'}
              </button>
            </div>
          </div>
        )}

        {!isPhotographer && capturedFacePreview && !cameraActive && (
          <div style={{
            marginBottom: '1.5rem',
            padding: '0.85rem 1rem',
            background: 'rgba(124,58,237,0.08)',
            border: '1px solid rgba(124,58,237,0.22)',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            flexWrap: 'wrap',
          }}>
            <img
              src={capturedFacePreview}
              alt="Visage scanné"
              style={{
                width: '52px',
                height: '52px',
                borderRadius: '50%',
                objectFit: 'cover',
                border: '2px solid #7c3aed',
              }}
            />
            <div>
              <strong style={{ color: '#7c3aed' }}>Visage webcam utilisé pour cette recherche</strong>
              <p style={{ margin: '0.15rem 0 0', color: 'var(--gray)', fontSize: '0.9rem' }}>
                Vous pouvez relancer un scan webcam si vous souhaitez utiliser une nouvelle capture.
              </p>
            </div>
          </div>
        )}

        {!isPhotographer && (
          <div style={{
            marginBottom: '1.5rem',
            padding: '1rem 1.25rem',
            background: 'var(--white)',
            border: '1px solid var(--light-gray)',
            borderRadius: '12px',
            boxShadow: '0 3px 10px var(--shadow)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '1rem',
            flexWrap: 'wrap',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem', color: 'var(--dark-gray)' }}>
              <FaShoppingCart style={{ color: 'var(--primary-orange)', fontSize: '1.25rem' }} />
              <strong>{cartPhotoIds.length} photo{cartPhotoIds.length !== 1 ? 's' : ''} in cart</strong>
              <span style={{ color: 'var(--gray)' }}>{cartTotal.toFixed(2)} F</span>
            </div>
            <button
              onClick={() => setShowPaymentForm(true)}
              disabled={cartPhotoIds.length === 0}
              style={{
                padding: '0.7rem 1.2rem',
                background: cartPhotoIds.length === 0 ? 'var(--gray)' : 'var(--primary-orange)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: cartPhotoIds.length === 0 ? 'not-allowed' : 'pointer',
                fontWeight: '700',
                display: 'flex',
                alignItems: 'center',
                gap: '0.45rem',
              }}
            >
              <FaCreditCard /> Payer maintenant
            </button>
          </div>
        )}

        {/* Scan results summary banner */}
        {scanResult && (
          <div style={{
            padding: '1rem 1.5rem', marginBottom: '1.5rem',
            background: 'linear-gradient(135deg, rgba(124,58,237,0.08), rgba(124,58,237,0.02))',
            border: '1px solid rgba(124,58,237,0.3)', borderRadius: '12px',
            display: 'flex', alignItems: 'center', gap: '1.5rem', flexWrap: 'wrap',
          }}>
            <FaSearch style={{ color: '#7c3aed', fontSize: '1.4rem', flexShrink: 0 }} />
            <div>
              <strong style={{ color: '#7c3aed' }}>
                {scanResult.model ? `Scan DeepFace - ${scanResult.model}` : 'Scan du visage'} terminé
              </strong>
              <span style={{ color: 'var(--dark-gray)', marginLeft: '0.5rem' }}>
                {scanResult.matched_photos.length} match{scanResult.matched_photos.length !== 1 ? 'es' : ''} found
                {' '}out of {scanResult.total_scanned} photos scanned
                {scanResult.new_matches > 0 && (
                  <span style={{ color: 'var(--primary-green)', marginLeft: '0.5rem' }}>
                    (+{scanResult.new_matches} new)
                  </span>
                )}
                {scanResult.skipped > 0 && (
                  <span style={{ color: 'var(--gray)', marginLeft: '0.5rem' }}>
                    · {scanResult.skipped} skipped
                  </span>
                )}
              </span>
            </div>
          </div>
        )}

        {displayedPhotos.length === 0 && showOnlyMatches ? (
          <div style={{
            padding: '4rem', textAlign: 'center',
            background: 'var(--white)', borderRadius: '15px',
            boxShadow: '0 4px 8px var(--shadow)'
          }}>
            <FaUser style={{ fontSize: '4rem', color: '#7c3aed', marginBottom: '1rem' }} />
            <h3 style={{ color: 'var(--dark-gray)', marginBottom: '1rem' }}>Aucune correspondance trouvée</h3>
            <p style={{ color: 'var(--gray)' }}>Your face was not detected in any photo from this event.</p>
          </div>
        ) : photos.length === 0 ? (
          <div style={{
            padding: '4rem', textAlign: 'center',
            background: 'var(--white)', borderRadius: '15px',
            boxShadow: '0 4px 8px var(--shadow)'
          }}>
            <FaImages style={{ fontSize: '4rem', color: 'var(--gray)', marginBottom: '1rem' }} />
            <h3 style={{ color: 'var(--dark-gray)', marginBottom: '1rem' }}>Aucune photo pour le moment</h3>
            <p style={{ color: 'var(--gray)' }}>Importez des photos dans cet événement pour les voir ici</p>
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
            gap: '1.5rem'
          }}>
            {displayedPhotos.map((photo) => {
              const index = photos.findIndex(p => p.id === photo.id);
              const isSelected = selectedPhotos.includes(photo.id);
              const isInCart = cartPhotoIds.includes(photo.id);
              const canDownload = canDownloadPhoto(photo, isEventOwner);
              const blobSrc = photoBlobs[photo.id];
              const matchData = scanResult?.matched_photos.find(m => m.id === photo.id);
              return (
                <div
                  key={photo.id}
                  style={{
                    position: 'relative', background: 'white',
                    borderRadius: '12px', overflow: 'hidden',
                    boxShadow: isSelected
                      ? '0 0 0 4px var(--primary-orange)'
                      : matchData
                        ? '0 0 0 3px #7c3aed, 0 4px 12px rgba(124,58,237,0.25)'
                        : '0 4px 8px var(--shadow)',
                    transition: 'all 0.3s ease'
                  }}
                >
                  {/* Selection checkbox */}
                  <button
                    onClick={() => togglePhotoSelection(photo.id)}
                    style={{
                      position: 'absolute', top: '8px', left: '8px',
                      background: 'rgba(0,0,0,0.45)', border: 'none',
                      color: isSelected ? 'var(--primary-orange)' : 'white',
                      fontSize: '1.3rem', cursor: 'pointer', zIndex: 10,
                      borderRadius: '6px', padding: '4px',
                      display: 'flex', alignItems: 'center',
                    }}
                  >
                    {isSelected ? <FaCheckSquare /> : <FaSquare />}
                  </button>

                  {/* Expand / view full size button */}
                  <button
                    onClick={() => openLightbox(index)}
                    style={{
                      position: 'absolute', top: '8px', right: '8px',
                      background: 'rgba(0,0,0,0.45)', border: 'none',
                      color: 'white', fontSize: '1rem', cursor: 'pointer',
                      zIndex: 10, borderRadius: '6px', padding: '6px 8px',
                      display: 'flex', alignItems: 'center',
                    }}
                  >
                    <FaExpand />
                  </button>

                  {/* Visibility badge */}
                  {photo.visibility === 'private' && (
                    <div style={{
                      position: 'absolute', top: '48px', right: '8px',
                      background: 'rgba(0,0,0,0.6)', color: 'white',
                      fontSize: '0.75rem', padding: '2px 7px', borderRadius: '10px',
                      zIndex: 10, display: 'flex', alignItems: 'center', gap: '4px',
                    }}>
                      <FaEyeSlash /> Privé
                    </div>
                  )}
                  {photo.visibility === 'public' && (
                    <div style={{
                      position: 'absolute', top: '48px', right: '8px',
                      background: 'rgba(0,120,0,0.6)', color: 'white',
                      fontSize: '0.75rem', padding: '2px 7px', borderRadius: '10px',
                      zIndex: 10, display: 'flex', alignItems: 'center', gap: '4px',
                    }}>
                      <FaEye /> Public
                    </div>
                  )}

                  {matchData && (
                    <div style={{
                      position: 'absolute', bottom: '8px', left: '8px',
                      background: 'rgba(124,58,237,0.9)', color: 'white',
                      fontSize: '0.75rem', padding: '3px 8px', borderRadius: '10px',
                      zIndex: 10, display: 'flex', alignItems: 'center', gap: '4px',
                      fontWeight: '700',
                    }}>
                      <FaUser /> {matchData.confidence_score.toFixed(1)}% match
                    </div>
                  )}

                  <img
                    src={blobSrc || 'https://placehold.co/300x200/png'}
                    alt={photo.filename}
                    onClick={() => openLightbox(index)}
                    style={{
                      width: '100%', height: '200px', objectFit: 'cover',
                      cursor: 'zoom-in', display: 'block',
                    }}
                    onError={(e) => { e.target.src = 'https://placehold.co/300x200/png'; }}
                  />

                  <div style={{ padding: '1rem' }}>
                    <div
                      title={getPhotoDisplayName(photo)}
                      style={{
                        color: 'var(--dark-gray)',
                        fontSize: '0.95rem',
                        fontWeight: '700',
                        marginBottom: '0.75rem',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {getPhotoDisplayName(photo)}
                    </div>

                    <div style={{
                      display: 'flex', justifyContent: 'space-between',
                      fontSize: '0.9rem', color: 'var(--gray)', marginBottom: '0.5rem'
                    }}>
                      <span>📊 {toNumber(photo.matches_count)} matches</span>
                      <span>💾 {toNumber(photo.file_size_mb).toFixed(2)} MB</span>
                    </div>

                    <div style={{
                      display: 'flex', justifyContent: 'space-between',
                      fontSize: '0.9rem', color: 'var(--gray)', marginBottom: '0.75rem'
                    }}>
                      <span>⬇️ {toNumber(photo.downloads_count)} downloads</span>
                      <span>{new Date(photo.uploaded_at).toLocaleDateString()}</span>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem', gap: '0.5rem' }}>
                      <div style={{
                        display: 'inline-block', padding: '0.3rem 0.8rem',
                        borderRadius: '20px', fontSize: '0.85rem', fontWeight: '700',
                        background: photo.price > 0 ? 'var(--primary-orange)' : 'var(--primary-green)',
                        color: 'white',
                      }}>
                      {photo.price > 0 ? `${parseFloat(photo.price).toFixed(2)} F` : 'Gratuit'}
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleToggleLike(photo); }}
                        title={photo.is_liked ? 'Unlike this photo' : 'Like this photo'}
                        style={{
                          padding: '0.3rem 0.7rem',
                          background: photo.is_liked ? '#FF6B35' : '#f4f4f4',
                          color: photo.is_liked ? 'white' : 'var(--gray)',
                          border: photo.is_liked ? 'none' : '1px solid #e0e0e0',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '0.85rem',
                          fontWeight: '700',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.3rem',
                        }}
                      >
                        <FaHeart /> {toNumber(photo.likes_count)}
                      </button>
                      {canDownload ? (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDownloadSingle(photo); }}
                          title="Télécharger cette photo"
                          style={{
                            padding: '0.3rem 0.7rem', background: 'var(--primary-orange)',
                            color: 'white', border: 'none', borderRadius: '6px',
                            cursor: 'pointer', fontSize: '0.85rem',
                            display: 'flex', alignItems: 'center', gap: '0.3rem',
                          }}
                        >
                          <FaDownload /> {photo.has_purchased && !isPhotographer ? 'Achetée' : ''}
                        </button>
                      ) : (
                        <button
                          onClick={(e) => { e.stopPropagation(); toggleCartPhoto(photo); }}
                          title={isInCart ? 'Retirer du panier' : 'Ajouter au panier'}
                          style={{
                            padding: '0.3rem 0.7rem',
                            background: isInCart ? 'var(--primary-green)' : 'var(--primary-orange)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '0.85rem',
                            fontWeight: '700',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.3rem',
                          }}
                        >
                          <FaShoppingCart /> {isInCart ? 'In Cart' : 'Add'}
                        </button>
                      )}
                    </div>

                    {isEventOwner && (
                      <div
                        style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}
                        onClick={e => e.stopPropagation()}
                      >
                        <button
                          onClick={() => setEditingPhoto(photo)}
                          style={{
                            flex: 1, padding: '0.5rem',
                            background: 'var(--primary-orange)', color: 'white',
                            border: 'none', borderRadius: '6px',
                            cursor: 'pointer', fontSize: '0.85rem', fontWeight: '600',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem',
                          }}
                        >
                          <FaEdit /> Modifier
                        </button>
                        <button
                          onClick={() => handleDeletePhoto(photo.id)}
                          disabled={deletingPhoto === photo.id}
                          style={{
                            flex: 1, padding: '0.5rem',
                            background: deletingPhoto === photo.id ? 'var(--gray)' : '#f44336',
                            color: 'white', border: 'none', borderRadius: '6px',
                            cursor: 'pointer', fontSize: '0.85rem', fontWeight: '600',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem',
                          }}
                        >
                          <FaTrash /> {deletingPhoto === photo.id ? '...' : 'Supprimer'}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {photos.length > 0 && (
          <div style={{
            marginTop: '3rem', padding: '2rem',
            background: 'var(--off-white)', borderRadius: '12px', textAlign: 'center'
          }}>
            <h3 style={{ color: 'var(--primary-green)', marginBottom: '1rem' }}>Statistiques photo</h3>
            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem'
            }}>
              {[
                { value: photos.length, label: 'Photos au total' },
                { value: photos.reduce((s, p) => s + toNumber(p.matches_count), 0), label: 'Correspondances au total' },
                { value: photos.reduce((s, p) => s + toNumber(p.downloads_count), 0), label: 'Téléchargements au total' },
                { value: `${photos.reduce((s, p) => s + toNumber(p.file_size_mb), 0).toFixed(2)} MB`, label: 'Taille totale' },
              ].map(({ value, label }) => (
                <div key={label}>
                  <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--primary-orange)' }}>{value}</div>
                  <div style={{ color: 'var(--gray)' }}>{label}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Lightbox / Slideshow */}
      {lightboxIndex !== null && currentPhoto && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.95)',
            zIndex: 2000, display: 'flex', flexDirection: 'column',
          }}
          onClick={closeLightbox}
        >
          {/* Top bar */}
          <div
            style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '1rem 1.5rem',
              background: 'linear-gradient(to bottom, rgba(0,0,0,0.7), transparent)',
              position: 'absolute', top: 0, left: 0, right: 0, zIndex: 10,
            }}
            onClick={e => e.stopPropagation()}
          >
            <span style={{ color: 'white', fontWeight: '600', fontSize: '1rem' }}>
              {lightboxIndex + 1} / {photos.length}
              {event?.name && <span style={{ marginLeft: '1rem', opacity: 0.7 }}>{event.name}</span>}
            </span>
            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
              {canDownloadPhoto(currentPhoto, isEventOwner) && (
                <button
                  onClick={() => handleDownloadSingle(currentPhoto)}
                  title="Télécharger cette photo"
                  style={{
                    background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white',
                    padding: '8px 14px', borderRadius: '8px', cursor: 'pointer', fontSize: '1.1rem',
                    display: 'flex', alignItems: 'center', gap: '6px',
                  }}
                >
                  <FaDownload /> Télécharger
                </button>
              )}
              <button
                onClick={() => setSlideshowActive(s => !s)}
                title={slideshowActive ? 'Pause slideshow' : 'Play slideshow'}
                style={{
                  background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white',
                  padding: '8px 14px', borderRadius: '8px', cursor: 'pointer', fontSize: '1.1rem',
                  display: 'flex', alignItems: 'center', gap: '6px',
                }}
              >
                {slideshowActive ? <FaPause /> : <FaPlay />}
                {slideshowActive ? 'Pause' : 'Play'}
              </button>
              <button
                onClick={closeLightbox}
                style={{
                  background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white',
                  padding: '8px 14px', borderRadius: '8px', cursor: 'pointer', fontSize: '1.2rem',
                }}
              >
                <FaTimes />
              </button>
            </div>
          </div>

          {/* Prev button */}
          <button
            onClick={(e) => { e.stopPropagation(); lightboxPrev(); }}
            style={{
              position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)',
              background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white',
              fontSize: '1.8rem', padding: '14px 18px', borderRadius: '50%',
              cursor: 'pointer', zIndex: 10,
            }}
          >
            <FaArrowLeft />
          </button>

          {/* Image */}
          <div
            style={{
              flex: 1,
              minHeight: '100vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '88px clamp(12px, 7vw, 80px) 118px',
              boxSizing: 'border-box',
            }}
            onClick={e => e.stopPropagation()}
          >
            <img
              src={photoBlobs[currentPhoto.id] || 'https://placehold.co/800x600/png'}
              alt={currentPhoto.filename}
              style={{
                display: 'block',
                width: 'auto',
                height: 'auto',
                maxWidth: 'calc(100vw - clamp(24px, 14vw, 160px))',
                maxHeight: 'calc(100vh - 206px)',
                objectFit: 'contain',
                borderRadius: '4px',
                boxShadow: '0 8px 40px rgba(0,0,0,0.6)',
              }}
            />
          </div>

          {/* Next button */}
          <button
            onClick={(e) => { e.stopPropagation(); lightboxNext(); }}
            style={{
              position: 'absolute', right: '16px', top: '50%', transform: 'translateY(-50%)',
              background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white',
              fontSize: '1.8rem', padding: '14px 18px', borderRadius: '50%',
              cursor: 'pointer', zIndex: 10,
            }}
          >
            <FaArrowRight />
          </button>

          {/* Bottom info */}
          <div
            style={{
              position: 'absolute', bottom: 0, left: 0, right: 0,
              padding: '1rem 1.5rem',
              background: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent)',
              color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              minHeight: '86px',
              boxSizing: 'border-box',
              gap: '1rem',
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ fontSize: '0.9rem', opacity: 0.85 }}>
              {currentPhoto.price > 0
                ? `${parseFloat(currentPhoto.price).toFixed(2)} F`
                : 'Gratuit'}
              {' · '}
              {currentPhoto.file_size_mb?.toFixed(2)} MB
            </div>

            {/* Slideshow progress bar */}
            {slideshowActive && (
              <div style={{
                position: 'absolute', bottom: 0, left: 0, right: 0,
                height: '3px', background: 'rgba(255,255,255,0.2)',
              }}>
                <div
                  key={lightboxIndex}
                  style={{
                    height: '100%', background: 'var(--primary-orange)',
                    animation: 'lbProgress 4s linear forwards',
                  }}
                />
              </div>
            )}

            {/* Thumbnails strip */}
            <div style={{ display: 'flex', gap: '6px', overflowX: 'auto', maxWidth: '60vw', padding: '2px' }}>
              {photos.map((p, i) => (
                <img
                  key={p.id}
                  src={photoBlobs[p.id] || 'https://placehold.co/60x40/png'}
                  alt=""
                  onClick={() => setLightboxIndex(i)}
                  style={{
                    width: '52px', height: '36px', objectFit: 'cover',
                    borderRadius: '4px', cursor: 'pointer', flexShrink: 0,
                    outline: i === lightboxIndex ? '2px solid var(--primary-orange)' : '2px solid transparent',
                    opacity: i === lightboxIndex ? 1 : 0.55,
                    transition: 'all 0.2s',
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes lbProgress {
          from { width: 0%; }
          to { width: 100%; }
        }
      `}</style>

      <PhotoEditModal
        photo={editingPhoto}
        blobUrl={editingPhoto ? photoBlobs[editingPhoto.id] : null}
        onClose={() => setEditingPhoto(null)}
        onSaved={handlePhotoSaved}
      />

      {showPaymentForm && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.55)',
            zIndex: 1100,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1rem',
          }}
          onClick={() => !checkingOut && setShowPaymentForm(false)}
        >
          <form
            onSubmit={handleCheckoutCart}
            style={{
              width: '100%',
              maxWidth: '520px',
              maxHeight: '90vh',
              overflowY: 'auto',
              background: 'white',
              borderRadius: '14px',
              padding: '1.5rem',
              boxShadow: '0 12px 40px rgba(0,0,0,0.25)',
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', alignItems: 'center', marginBottom: '1rem' }}>
              <div>
                <h3 style={{ margin: 0, color: 'var(--primary-orange)' }}>Paiement</h3>
                <p style={{ margin: '0.25rem 0 0', color: 'var(--gray)', fontSize: '0.9rem' }}>
                  {cartPhotoIds.length} photo{cartPhotoIds.length !== 1 ? 's' : ''} · {cartTotal.toFixed(2)} F
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowPaymentForm(false)}
                disabled={checkingOut}
                style={{
                  border: 'none',
                  background: 'var(--off-white)',
                  color: 'var(--dark-gray)',
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  cursor: checkingOut ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <FaTimes />
              </button>
            </div>

            <div style={{
              padding: '0.85rem',
              background: 'var(--off-white)',
              borderRadius: '10px',
              marginBottom: '1rem',
              display: 'grid',
              gap: '0.5rem',
            }}>
              {cartPhotos.map(photo => (
                <div key={photo.id} style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', color: 'var(--dark-gray)', fontSize: '0.9rem' }}>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {getPhotoDisplayName(photo)}
                  </span>
                  <strong>{Number(photo.price || 0).toFixed(2)} F</strong>
                </div>
              ))}
            </div>

            {paymentError && (
              <div className="error-message" style={{ marginBottom: '1rem' }}>{paymentError}</div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.5rem', marginBottom: '1rem' }}>
              {Object.entries(PAYMENT_METHODS).map(([method, label]) => (
                <button
                  key={method}
                  type="button"
                  onClick={() => handlePaymentChange('method', method)}
                  style={{
                    border: paymentData.method === method ? '2px solid var(--primary-orange)' : '1px solid var(--light-gray)',
                    background: paymentData.method === method ? 'rgba(255,122,0,0.08)' : 'white',
                    color: 'var(--dark-gray)',
                    borderRadius: '8px',
                    padding: '0.75rem 0.5rem',
                    cursor: 'pointer',
                    fontWeight: '700',
                    fontSize: '0.82rem',
                    minHeight: '54px',
                  }}
                >
                  {label}
                </button>
              ))}
            </div>

            {paymentData.method === 'visa' && (
              <>
                <div className="form-group">
                  <label>Nom du titulaire</label>
                  <input
                    type="text"
                    value={paymentData.cardholderName}
                    onChange={e => handlePaymentChange('cardholderName', e.target.value)}
                    placeholder="Nom sur la carte"
                    autoComplete="cc-name"
                  />
                </div>

                <div className="form-group">
                  <label>Numéro de carte</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={paymentData.cardNumber}
                    onChange={e => handlePaymentChange('cardNumber', e.target.value.replace(/[^\d ]/g, '').slice(0, 23))}
                    placeholder="4242 4242 4242 4242"
                    autoComplete="cc-number"
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                  <div className="form-group">
                    <label>Expiration</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={paymentData.expiry}
                      onChange={e => handlePaymentChange('expiry', e.target.value.replace(/[^\d/]/g, '').slice(0, 5))}
                      placeholder="MM/YY"
                      autoComplete="cc-exp"
                    />
                  </div>
                  <div className="form-group">
                    <label>CVC</label>
                    <input
                      type="password"
                      inputMode="numeric"
                      value={paymentData.cvc}
                      onChange={e => handlePaymentChange('cvc', e.target.value.replace(/\D/g, '').slice(0, 4))}
                      placeholder="123"
                      autoComplete="cc-csc"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>E-mail de facturation</label>
                  <input
                    type="email"
                    value={paymentData.billingEmail}
                    onChange={e => handlePaymentChange('billingEmail', e.target.value)}
                    placeholder="you@example.com"
                    autoComplete="email"
                  />
                </div>
              </>
            )}

            {(paymentData.method === 'wave_ci' || paymentData.method === 'orange_money_ci') && (
              <>
                <div className="form-group">
                  <label>Numéro {activePaymentLabel}</label>
                  <input
                    type="tel"
                    inputMode="tel"
                    value={paymentData.mobileNumber}
                    onChange={e => handlePaymentChange('mobileNumber', e.target.value.replace(/[^\d+ ]/g, '').slice(0, 18))}
                    placeholder="+225 07 00 00 00 00"
                    autoComplete="tel"
                  />
                </div>

                {paymentData.method === 'wave_ci' && (
                  <div style={{
                    border: '1px solid var(--light-gray)',
                    borderRadius: '10px',
                    padding: '1rem',
                    marginBottom: '1rem',
                    display: 'grid',
                    gridTemplateColumns: '112px 1fr',
                    gap: '1rem',
                    alignItems: 'center',
                    background: '#f8fbff',
                  }}>
                    <div style={{
                      width: '112px',
                      height: '112px',
                      display: 'grid',
                      gridTemplateColumns: 'repeat(9, 1fr)',
                      gridTemplateRows: 'repeat(9, 1fr)',
                      gap: '3px',
                      padding: '8px',
                      background: 'white',
                      border: '1px solid #d8e6f8',
                    }}>
                      {waveQrCells.map((active, index) => (
                        <span
                          key={index}
                          style={{
                            background: active ? '#111827' : '#fff',
                            borderRadius: '2px',
                          }}
                        />
                      ))}
                    </div>
                    <div>
                      <strong style={{ color: 'var(--dark-gray)' }}>QR code Wave</strong>
                      <p style={{ margin: '0.35rem 0 0', color: 'var(--gray)', fontSize: '0.9rem' }}>
                        Scannez ce code dans Wave pour payer {cartTotal.toFixed(2)} F.
                      </p>
                    </div>
                  </div>
                )}

                {paymentData.method === 'orange_money_ci' && (
                  <div style={{
                    border: '1px solid #ffd2a8',
                    borderRadius: '10px',
                    padding: '1rem',
                    marginBottom: '1rem',
                    background: '#fff7ed',
                    color: 'var(--dark-gray)',
                  }}>
                    <strong>Message de paiement Orange Money</strong>
                    <p style={{ margin: '0.35rem 0 0', color: 'var(--gray)', fontSize: '0.9rem' }}>
                      Une demande de paiement de {cartTotal.toFixed(2)} F sera envoyée à {paymentData.mobileNumber || 'votre numéro Orange'}.
                    </p>
                  </div>
                )}
              </>
            )}

            <button
              type="submit"
              disabled={checkingOut}
              className="btn-submit"
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '0.45rem',
              }}
            >
              <FaCreditCard /> {checkingOut ? 'Traitement du paiement...' : `Payer ${cartTotal.toFixed(2)} F avec ${activePaymentLabel}`}
            </button>
          </form>
        </div>
      )}
    </>
  );
};

export default EventBrowse;
